/**
 * Black Elm morphing desktop nav — one panel that slides/resizes between sections.
 * Mobile keeps Dawn drawer (no morph on coarse pointer / narrow viewport).
 */
(function () {
  const DESKTOP_MIN = 990;
  const CLOSE_DELAY_MS = 120;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isDesktop() {
    return window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`).matches
      && !window.matchMedia('(pointer: coarse)').matches;
  }

  function init() {
    const root = document.querySelector('[data-be-morph-nav]');
    const panel = document.getElementById('beMorphPanel');
    if (!root || !panel) return;

    if (!isDesktop()) return;
    document.body.classList.add('be-morph-nav-enabled');

    const triggers = [...root.querySelectorAll('.be-morph-trigger')];
    const sections = [...panel.querySelectorAll('[data-be-morph-section]')];
    let closeTimer = null;
    let activeId = null;

    function sectionFor(id) {
      return sections.find((s) => s.getAttribute('data-be-morph-section') === id) || null;
    }

    function setExpanded(trigger, expanded) {
      triggers.forEach((t) => t.setAttribute('aria-expanded', t === trigger && expanded ? 'true' : 'false'));
    }

    function measureSection(section) {
      // hidden on the panel or the section is display:none, so size reads as 0.
      const restorePanelHidden = panel.hidden;
      panel.hidden = false;
      section.removeAttribute('hidden');
      section.classList.add('is-measuring');
      const w = Math.max(section.scrollWidth, 180);
      const h = Math.max(section.scrollHeight, 44);
      section.classList.remove('is-measuring');
      if (restorePanelHidden) panel.hidden = true;
      return { w, h };
    }

    function positionPanel(trigger, section) {
      const rect = trigger.getBoundingClientRect();
      const { w, h } = measureSection(section);
      const padX = 20;
      const padY = 16;
      const width = w + padX;
      const height = h + padY;
      let left = rect.left + rect.width / 2 - width / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
      const top = rect.bottom + 10;
      const firstOpen = !panel.classList.contains('is-open');
      panel.classList.toggle('be-morph-panel--instant', prefersReducedMotion() || firstOpen);

      sections.forEach((s) => {
        if (s === section) {
          s.removeAttribute('hidden');
          s.classList.add('is-active');
          s.classList.remove('is-leaving');
        } else if (s.classList.contains('is-active')) {
          s.removeAttribute('hidden');
          s.classList.remove('is-active');
          s.classList.add('is-leaving');
        } else {
          s.setAttribute('hidden', '');
          s.classList.remove('is-active', 'is-leaving');
        }
      });

      panel.hidden = false;
      panel.style.left = `${left}px`;
      panel.style.top = `${top}px`;
      panel.style.width = `${width}px`;
      panel.style.height = `${height}px`;
      panel.classList.add('is-open');
      activeId = section.getAttribute('data-be-morph-section') || null;

      if (firstOpen && !prefersReducedMotion()) {
        requestAnimationFrame(() => panel.classList.remove('be-morph-panel--instant'));
      }
    }

    function open(trigger) {
      clearTimeout(closeTimer);
      const id = trigger.getAttribute('data-be-morph-target');
      const section = sectionFor(id);
      if (!section) return;
      setExpanded(trigger, true);
      positionPanel(trigger, section);
    }

    function close() {
      panel.classList.remove('is-open');
      panel.hidden = true;
      sections.forEach((s) => {
        s.classList.remove('is-active', 'is-leaving');
        s.setAttribute('hidden', '');
      });
      setExpanded(null, false);
      activeId = null;
    }

    function scheduleClose() {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(close, CLOSE_DELAY_MS);
    }

    triggers.forEach((trigger, index) => {
      trigger.addEventListener('mouseenter', () => open(trigger));
      trigger.addEventListener('focus', () => open(trigger));
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          triggers[(index + 1) % triggers.length].focus();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          triggers[(index - 1 + triggers.length) % triggers.length].focus();
        } else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open(trigger);
        } else if (e.key === 'Escape') {
          close();
          trigger.blur();
        }
      });
    });

    root.addEventListener('mouseleave', scheduleClose);
    panel.addEventListener('mouseenter', () => clearTimeout(closeTimer));
    panel.addEventListener('mouseleave', scheduleClose);

    panel.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        close();
        const t = triggers.find((tr) => tr.getAttribute('data-be-morph-target') === activeId);
        t?.focus();
      }
    });

    document.addEventListener('click', (e) => {
      if (!root.contains(e.target) && !panel.contains(e.target)) close();
    });

    window.addEventListener('resize', () => {
      if (!isDesktop()) close();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
